import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useNavigate } from 'react-router-dom'; // Assuming useNavigate is needed as per changes

interface Sermon {
  id: string;
  title: string;
  scripture: string;
  content: string;
  date: string | null;
  status: 'draft' | 'complete';
  series_id: string | null;
  archived: boolean;
  youtube_url: string | null;
  user_permission?: 'owner' | 'editor' | 'viewer';
  shared_by?: {
    id: string;
    email: string;
    full_name?: string;
  };
  team_name?: string;
  series?: {
    id: string;
    title: string;
    description: string | null;
    created_at: string;
  };
  shared_with?: {
    user_id: string;
    full_name: string;
  }[];
  has_share_page?: boolean;
}

// Define SharedSermon if it's used and not defined elsewhere
interface SharedSermon extends Sermon {
  shared_by: { // Ensure shared_by is defined as per its usage
    id: string;
    email: string;
    full_name?: string;
  };
}


interface Series {
  id: string;
  title: string;
  description: string | null;
  created_at: string;
}

export function useSermons() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sermons, setSermons] = useState<Sermon[]>([]);
  const [series, setSeries] = useState<Series[]>([]);
  const [sharedSermons, setSharedSermons] = useState<SharedSermon[]>([]); // This state might not be used in the provided snippet, but kept as per changes
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'complete'>('all');
  const [seriesFilter, setSeriesFilter] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);

  const hasLoadedOnce = useRef(false);

  const loadSermons = async () => {
    try {
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      setError(null);

      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Loading sermons for user:', user.id);

      // First, load all series
      const { data: seriesData, error: seriesError } = await supabase
        .from('series')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (seriesError) throw seriesError;
      setSeries(seriesData || []);
      console.log('Series loaded:', seriesData?.length || 0);

      // Build the base query for own sermons
      let ownSermonsQuery = supabase
        .from('sermons')
        .select(`
          *,
          series:series_id (
            id,
            title,
            description
          )
        `)
        .eq('user_id', user.id)
        .eq('archived', showArchived);

      // Fetch team-shared sermons for the current user
      let sharedSermons: any[] = [];

      // Step 1: get the teams this user belongs to as an accepted member
      const { data: memberTeams } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', user.id)
        .eq('status', 'accepted');

      const memberTeamIds = (memberTeams || []).map((m: any) => m.team_id);

      // Step 2: only query team_sermon_shares if the user is in at least one team
      // (Supabase rejects .in() with an empty array)
      let teamShareData: any[] = [];
      let teamShareError: any = null;

      if (memberTeamIds.length > 0) {
        const result = await supabase
          .from('team_sermon_shares')
          .select(`
            sermon_id,
            permission,
            shared_by,
            team:teams (
              id,
              name,
              created_by
            )
          `)
          .in('team_id', memberTeamIds);

        teamShareData = result.data || [];
        teamShareError = result.error;
      }

      if (teamShareError) {
        console.error('Error fetching team sermon shares:', teamShareError);
      }

      console.log('Team share data for user:', user.id, teamShareData);

      if (teamShareData.length > 0) {
        // Deduplicate sermon IDs (a sermon may be shared via multiple teams)
        const seenSermonIds = new Map<string, { permission: 'editor' | 'viewer'; teamName: string; sharedBy: string }>();
        for (const share of teamShareData) {
          const existing = seenSermonIds.get(share.sermon_id);
          const team = share.team as any;
          // If the sermon is already seen, upgrade to editor if any share grants it
          if (!existing || (share.permission === 'editor' && existing.permission !== 'editor')) {
            seenSermonIds.set(share.sermon_id, {
              permission: share.permission as 'editor' | 'viewer',
              teamName: team?.name || 'Your Team',
              sharedBy: share.shared_by,
            });
          }
        }

        // Don't show sermons the current user owns
        const uniqueSermonIds = Array.from(seenSermonIds.keys());

        if (uniqueSermonIds.length > 0) {
          const { data: sharedSermonsData, error: sharedSermonsError } = await supabase
            .from('sermons')
            .select(`
              *,
              series:series_id (
                id,
                title,
                description
              )
            `)
            .in('id', uniqueSermonIds)
            .neq('user_id', user.id); // exclude own sermons

          if (sharedSermonsError) {
            console.error('Error fetching team-shared sermons:', sharedSermonsError);
          } else {
            // Get profile info for the users who shared the sermons
            const sharedByIds = Array.from(new Set(Array.from(seenSermonIds.values()).map(s => s.sharedBy)));
            const { data: usersData } = await supabase.rpc('get_user_profiles', { user_ids: sharedByIds });

            sharedSermons = (sharedSermonsData || []).map(sermon => {
              const meta = seenSermonIds.get(sermon.id)!;
              const userInfo = usersData?.find((u: any) => u.user_id === meta.sharedBy);
              return {
                ...sermon,
                user_permission: meta.permission,
                team_name: meta.teamName,
                shared_by: userInfo
                  ? { id: userInfo.user_id, email: userInfo.email || '', full_name: userInfo.full_name || 'Unknown User' }
                  : { id: meta.sharedBy, email: '', full_name: 'Unknown User' },
              };
            });
          }
        }
      }

      // Legacy: also pick up any sermon_user_shares not yet migrated
      const { data: legacySharedData } = await supabase
        .from('sermon_user_shares')
        .select('sermon_id, shared_by_user_id')
        .eq('shared_with_user_id', user.id);

      if (legacySharedData && legacySharedData.length > 0) {
        const alreadyFetched = new Set(sharedSermons.map((s: any) => s.id));
        const legacyIds = legacySharedData.map(s => s.sermon_id).filter(id => !alreadyFetched.has(id));
        if (legacyIds.length > 0) {
          const { data: legacySermonsData } = await supabase
            .from('sermons')
            .select(`*, series:series_id (id, title, description)`)
            .in('id', legacyIds);

          const legacyByIds = legacySharedData.map(s => s.shared_by_user_id);
          const { data: legacyUsersData } = await supabase.rpc('get_user_profiles', { user_ids: legacyByIds });

          const legacyMapped = (legacySermonsData || []).map(sermon => {
            const shareInfo = legacySharedData.find(s => s.sermon_id === sermon.id);
            const userInfo = legacyUsersData?.find((u: any) => u.user_id === shareInfo?.shared_by_user_id);
            return {
              ...sermon,
              user_permission: 'viewer' as const,
              shared_by: userInfo
                ? { id: userInfo.user_id, email: userInfo.email || '', full_name: userInfo.full_name || 'Unknown User' }
                : { id: shareInfo?.shared_by_user_id || '', email: '', full_name: 'Unknown User' },
            };
          });
          sharedSermons = [...sharedSermons, ...legacyMapped];
        }
      }

      // Filter out any team-shared sermons the user has dismissed from their dashboard
      if (sharedSermons.length > 0) {
        const { data: dismissedData } = await supabase
          .from('dismissed_team_sermons')
          .select('sermon_id')
          .eq('user_id', user.id);

        if (dismissedData && dismissedData.length > 0) {
          const dismissedIds = new Set(dismissedData.map((d: any) => d.sermon_id));
          sharedSermons = sharedSermons.filter(s => !dismissedIds.has(s.id));
        }
      }

      // Apply filters to own sermons
      if (debouncedSearchQuery) {
        ownSermonsQuery = ownSermonsQuery.or(`title.ilike.%${debouncedSearchQuery}%,scripture.ilike.%${debouncedSearchQuery}%`);
      }

      if (statusFilter !== 'all') {
        ownSermonsQuery = ownSermonsQuery.eq('status', statusFilter);
      }

      if (seriesFilter !== 'all') {
        if (seriesFilter === 'no-series') {
          ownSermonsQuery = ownSermonsQuery.is('series_id', null);
        } else {
          ownSermonsQuery = ownSermonsQuery.eq('series_id', seriesFilter);
        }
      }

      // Order by updated_at first, then by date
      ownSermonsQuery = ownSermonsQuery.order('updated_at', { ascending: false });

      // Execute the query for own sermons
      const { data: ownSermonsData, error: ownSermonsError } = await ownSermonsQuery;

      if (ownSermonsError) {
        console.error('Error fetching own sermons:', ownSermonsError);
        throw ownSermonsError;
      }

      console.log('Own sermons loaded:', ownSermonsData?.length || 0);
      console.log('Sermons data:', ownSermonsData);

      // Check for existing share pages
      let sharePageData: any[] = [];
      if (ownSermonsData && ownSermonsData.length > 0) {
        const sermonIds = ownSermonsData.map(sermon => sermon.id);
        const { data: sharePages, error: shareError } = await supabase
          .from('shared_sermons')
          .select('sermon_id')
          .in('sermon_id', sermonIds);

        if (shareError) {
          console.error('Error fetching share pages:', shareError);
        } else {
          sharePageData = sharePages || [];
        }
      }

      // Fetch sharing information for owned sermons
      let ownSermonsWithSharing = ownSermonsData || [];
      if (ownSermonsData && ownSermonsData.length > 0) {
        const sermonIds = ownSermonsData.map(sermon => sermon.id);

        // Get sharing information for owned sermons
        const { data: sharingData, error: sharingError } = await supabase
          .from('sermon_user_shares')
          .select('sermon_id, shared_with_user_id')
          .in('sermon_id', sermonIds);

        let enrichedSharingData: any[] = [];
        if (sharingData && sharingData.length > 0) {
          // Get user profiles for shared with users
          const sharedWithUserIds = sharingData.map(share => share.shared_with_user_id);
          const { data: userProfilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, full_name')
            .in('id', sharedWithUserIds);

          if (profilesError) {
            console.error('Error fetching user profiles:', profilesError);
          }

          // Combine the data
          enrichedSharingData = sharingData.map(share => ({
            ...share,
            shared_with_user: userProfilesData?.find(profile => profile.id === share.shared_with_user_id)
          }));
        }

        if (sharingError) {
          console.error('Error fetching sharing data:', sharingError);
        } else if (enrichedSharingData.length > 0) {
          // Group sharing data by sermon_id
          const sharingBySermon = enrichedSharingData.reduce((acc, share) => {
            if (!acc[share.sermon_id]) {
              acc[share.sermon_id] = [];
            }
            acc[share.sermon_id].push({
              user_id: share.shared_with_user_id,
              full_name: share.shared_with_user?.full_name || share.shared_with_user?.first_name && share.shared_with_user?.last_name 
                ? `${share.shared_with_user.first_name} ${share.shared_with_user.last_name}`
                : share.shared_with_user?.first_name || share.shared_with_user?.last_name || 'Unknown User'
            });
            return acc;
          }, {} as Record<string, Array<{ user_id: string; full_name: string }>>);

          // Add sharing information to owned sermons
          ownSermonsWithSharing = ownSermonsData.map(sermon => ({
            ...sermon,
            shared_with: sharingBySermon[sermon.id] || [],
            has_share_page: sharePageData.some(sp => sp.sermon_id === sermon.id)
          }));
        } else {
          // No sharing data, but still add share page information
          ownSermonsWithSharing = ownSermonsData.map(sermon => ({
            ...sermon,
            shared_with: [],
            has_share_page: sharePageData.some(sp => sp.sermon_id === sermon.id)
          }));
        }
      }

      // Transform owned sermons
      const transformedOwnSermons = ownSermonsWithSharing?.map(sermon => {
        const transformedSermon = {
          ...sermon,
          user_permission: 'owner' as const
        };

        if (sermon.series_id && !sermon.series) {
          const seriesInfo = seriesData?.find(s => s.id === sermon.series_id);
          if (seriesInfo) {
            return {
              ...transformedSermon,
              series: {
                id: seriesInfo.id,
                title: seriesInfo.title,
                description: seriesInfo.description
              }
            };
          }
        }
        return transformedSermon;
      }) || [];

      // Transform shared sermons
      const transformedSharedSermons = sharedSermons.map(sermon => {
        const transformedSermon = {
          ...sermon
        };

        if (sermon.series_id && !sermon.series) {
          const seriesInfo = seriesData?.find(s => s.id === sermon.series_id);
          if (seriesInfo) {
            return {
              ...transformedSermon,
              series: {
                id: seriesInfo.id,
                title: seriesInfo.title,
                description: seriesInfo.description
              }
            };
          }
        }
        return transformedSermon;
      });

      // Combine and filter shared sermons based on current filters
      let filteredSharedSermons = transformedSharedSermons;

      if (debouncedSearchQuery) {
        filteredSharedSermons = filteredSharedSermons.filter(sermon => 
          sermon.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
          sermon.scripture.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
        );
      }

      if (statusFilter !== 'all') {
        filteredSharedSermons = filteredSharedSermons.filter(sermon => 
          sermon.status === statusFilter
        );
      }

      if (seriesFilter !== 'all') {
        if (seriesFilter === 'no-series') {
          filteredSharedSermons = filteredSharedSermons.filter(sermon => !sermon.series_id);
        } else {
          filteredSharedSermons = filteredSharedSermons.filter(sermon => sermon.series_id === seriesFilter);
        }
      }

      // Only show shared sermons if not viewing archived ones
      const finalSermons = showArchived 
        ? transformedOwnSermons 
        : [...transformedOwnSermons, ...filteredSharedSermons];

      setSermons(finalSermons);
    } catch (e) {
      console.error('Error in loadSermons:', e);
      setError(e instanceof Error ? e.message : 'Failed to load sermons');
      setSermons([]);
      setSeries([]);
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
    }
  };

  // Debounce search query to prevent interference with typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Increased to 500ms for better debouncing

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const deleteSermon = async (id: string) => {
    try {
      setError(null);
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error: deleteError } = await supabase
        .from('sermons')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      await loadSermons();
    } catch (e) {
      console.error('Error deleting sermon:', e);
      setError(e instanceof Error ? e.message : 'Failed to delete sermon');
    }
  };

  const toggleArchive = async (id: string, currentState: boolean) => {
    try {
      setError(null);
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error: updateError } = await supabase
        .from('sermons')
        .update({ archived: !currentState })
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;
      await loadSermons();
    } catch (e) {
      console.error('Error toggling archive:', e);
      setError(e instanceof Error ? e.message : 'Failed to update sermon');
    }
  };

  const createSeries = async (data: { title: string; description: string }) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: newSeries, error } = await supabase
        .from('series')
        .insert([{ ...data, user_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      await loadSermons();
      return newSeries;
    } catch (e) {
      console.error('Error creating series:', e);
      setError(e instanceof Error ? e.message : 'Failed to create series');
      return null;
    }
  };

  const deleteSeries = async (seriesId: string) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      await supabase
        .from('shared_series')
        .delete()
        .eq('series_id', seriesId);

      await supabase
        .from('sermons')
        .update({ series_id: null })
        .eq('series_id', seriesId)
        .eq('user_id', user.id);

      const { error } = await supabase
        .from('series')
        .delete()
        .eq('id', seriesId)
        .eq('user_id', user.id);

      if (error) throw error;

      if (seriesFilter === seriesId) {
        setSeriesFilter('all');
      }

      await loadSermons();
    } catch (e) {
      console.error('Error deleting series:', e);
      setError(e instanceof Error ? e.message : 'Failed to delete series');
    }
  };

  const fetchUserProfiles = useCallback(async (userIds: string[]) => {
    if (userIds.length === 0) return {};

    try {
      const { data, error } = await supabase.rpc('get_user_profiles', {
        user_ids: userIds
      });

      if (error) throw error;

      const profilesMap: Record<string, any> = {};
      data?.forEach((profile: any) => {
        profilesMap[profile.user_id] = {
          user_id: profile.user_id,
          email: profile.email,
          full_name: profile.full_name,
          first_name: profile.full_name?.split(' ')[0] || '',
          last_name: profile.full_name?.split(' ').slice(1).join(' ') || ''
        };
      });

      return profilesMap;
    } catch (err) {
      console.error('Error fetching user profiles:', err);
      return {};
    }
  }, []);

  useEffect(() => {
    if (user) {
      // Don't load sermons if we're in the middle of typing (searchQuery !== debouncedSearchQuery)
      if (searchQuery === debouncedSearchQuery) {
        loadSermons().catch(err => {
          console.error('Error in loadSermons:', err);
          setError(err instanceof Error ? err.message : 'Failed to load sermons');
        });
      }
    }
  }, [user, debouncedSearchQuery, statusFilter, seriesFilter, showArchived]); // Updated dependency array


  const stopSharing = async (sermonId: string, isTeamShared: boolean = false) => {
    try {
      setError(null);
      if (!user) {
        throw new Error('User not authenticated');
      }

      if (isTeamShared) {
        // Team-shared sermons: invoke RPC so the share row is fully deleted
        // (bypassing the owner-only DELETE RLS) after verifying team membership.
        // This reverts the owner's "shared" status so they can re-share if needed.
        const { error: rpcError } = await supabase.rpc('remove_team_sermon_share', {
          p_sermon_id: sermonId,
        });

        if (rpcError) {
          console.error('Error removing team sermon share:', rpcError);
          throw rpcError;
        }
      } else {
        // Legacy sermon_user_shares: remove the share row entirely.
        const { error: deleteError } = await supabase
          .from('sermon_user_shares')
          .delete()
          .eq('sermon_id', sermonId)
          .eq('shared_with_user_id', user.id);

        if (deleteError) {
          console.error('Delete error from sermon_user_shares:', deleteError);
          throw deleteError;
        }
      }

      // Immediately remove from local state
      setSermons(currentSermons =>
        currentSermons.filter(sermon =>
          !(sermon.id === sermonId && sermon.shared_by)
        )
      );

      // Refresh to ensure consistency
      await loadSermons();
    } catch (e) {
      console.error('Error removing sermon from dashboard:', e);
      setError(e instanceof Error ? e.message : 'Failed to remove sermon from dashboard');
      throw e;
    }
  };

  const removeUserSharing = async (sermonId: string, sharedWithUserId: string) => {
    try {
      setError(null);
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Removing user sharing for sermon:', sermonId, 'removing user:', sharedWithUserId);

      // Delete specific user share from sermon_user_shares table
      const { error: deleteError } = await supabase
        .from('sermon_user_shares')
        .delete()
        .eq('sermon_id', sermonId)
        .eq('shared_by_user_id', user.id)
        .eq('shared_with_user_id', sharedWithUserId);

      if (deleteError) {
        console.error('Delete error from sermon_user_shares:', deleteError);
        throw deleteError;
      }

      // Refresh to ensure consistency
      await loadSermons();

      console.log('Successfully removed user sharing');
    } catch (e) {
      console.error('Error removing user sharing:', e);
      setError(e instanceof Error ? e.message : 'Failed to remove user sharing');
      throw e;
    }
  };

  const shareSermon = useCallback(async (sermonId: string, userId: string, message?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // First verify the user owns this sermon with explicit user_id check
      const { data: sermon, error: sermonError } = await supabase
        .from('sermons')
        .select('user_id, title')
        .eq('id', sermonId)
        .eq('user_id', user.id) // Ensure ownership
        .single();

      if (sermonError) {
        console.error('Error verifying sermon ownership:', sermonError);
        throw new Error('Unable to verify sermon ownership. You can only share your own sermons.');
      }

      if (!sermon) {
        throw new Error('Sermon not found or you do not have permission to share it');
      }

      if (sermon.user_id !== user.id) {
        throw new Error('You can only share your own sermons');
      }

      // Verify the target user exists
      const { data: targetUser, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single();

      if (userError || !targetUser) {
        throw new Error('Target user not found');
      }

      const { error } = await supabase
        .from('sermon_user_shares')
        .insert({
          sermon_id: sermonId,
          shared_by_user_id: user.id,
          shared_with_user_id: userId,
          message
        });

      if (error) {
        console.error('Error inserting sermon share:', error);
        if (error.code === '23505') { // unique constraint violation
          throw new Error('Sermon is already shared with this user');
        }
        if (error.code === '23503') { // foreign key violation
          throw new Error('Invalid sermon or user reference');
        }
        throw new Error(`Failed to share sermon: ${error.message}`);
      }

      // Refresh sermons list to update UI
      loadSermons();
    } catch (error) {
      console.error('Error sharing sermon:', error);
      throw error;
    }
  }, [loadSermons]);

  return {
    sermons,
    series,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    seriesFilter,
    setSeriesFilter,
    showArchived,
    setShowArchived,
    deleteSermon,
    toggleArchive,
    createSeries,
    deleteSeries,
    stopSharing,
    removeUserSharing,
    shareSermon,
    refresh: loadSermons
  };
}